import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  REGION_BY_SFC_API,
  HEDGE_RATIO_FILTERS_API,
  HEDGE_RATIO_SCENARIO_MNEMONICS_API,
  HEDGE_RATIO_GAPRESULT_REPORT_API,
  SENSITIVITY_GAPRESULT_REPORT_API,
  LIQUIDITY_GAPRESULT_REPORT_API,
  GET_EXOGENEOUS_INPUT_DATA,
  MARKET_VALUE_REPORT_API,
  MULTILEDGER_FX_API,
  BALANCE_SHEET_REPORT_API,
  ADVANCED_NII_RESULT_REPORT_API,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";
import { HR_DENOMINATOR, HR_NUMERATOR } from "../utils/constants";
import { getAllowedMonthEnds } from "../utils/utils";

const HedgeRatioContext = createContext();

export const HedgeRatioProvider = ({ children }) => {
  const [selectedMonthEnd, setSelectedMonthEnd] = useState(null);
  const [selectedBusinessEntityID, setSelectedBusinessEntityID] =
    useState(null);
  const [selectedEntityID, setSelectedEntityID] = useState(null);
  const [localCurrency, setLocalCurrency] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedScenarioMnemonics, setSelectedScenarioMnemonics] =
    useState(null);
  const [modelDatabases, setModelDatabases] = useState([]);
  const [selectedModelDatabase, setSelectedModelDatabase] = useState(null);

  const [figureDivisors, setFigureDivisors] = useState([
    { name: "Raw", value: 1 },
    { name: "Thousands", value: 1000 },
    { name: "Millions", value: 1000000 },
    { name: "Billions", value: 1000000000 },
  ]);
  const [selectedFigureDivisor, setSelectedFigureDivisor] = useState(
    figureDivisors[0].value,
  );

  const [decimalsIn, setDecimalsIn] = useState([
    { name: "No Decimal", value: "0" },
    { name: "1 Decimal", value: "1" },
    { name: "2 Decimals", value: "2" },
    { name: "3 Decimals", value: "3" },
  ]);
  const [selectedDecimalsIn, setSelectedDecimalsIn] = useState(
    decimalsIn[0].value,
  );

  const [sumOfNumerators, setSumOfNumerators] = useState(null);
  const [sumOfDenominators, setSumOfDenominators] = useState(null);
  const [hedgeRatioPercentage, setHedgeRatioPercentage] = useState(null);

  const [region, setRegion] = useState(null);
  const [regionLoading, setRegionLoading] = useState(true);
  const [regionError, setRegionError] = useState(null);

  const [hrFiltersData, setHrFiltersData] = useState([]);
  const [hrFiltersDataLoading, setHrFiltersDataLoading] = useState(false);
  const [hrFiltersDataError, setHrFiltersDataError] = useState(null);

  const [scenarioMnemonicsData, setScenarioMnemonicsData] = useState([]);
  const [scenarioMnemonicsLoading, setScenarioMnemonicsLoading] =
    useState(false);
  const [scenarioMnemonicsError, setScenarioMnemonicsError] = useState(null);

  const [hrGapResultData, setHrGapResultData] = useState([]);
  const [numeratorAccountNum, setNumeratorAccountNum] = useState(null);
  const [denominatorAccountNum, setDenominatorAccountNum] = useState(null);
  const [hrGapResultDataLoading, setHrGapResultDataLoading] = useState(false);
  const [hrGapResultDataError, setHrGapResultDataError] = useState(null);

  const [advancedNIIResultData, setAdvancedNIIResultData] = useState([]);
  const [advancedNIIResultDataLoading, setAdvancedNIIResultDataLoading] =
    useState(false);
  const [advancedNIIResultDataError, setAdvancedNIIResultDataError] =
    useState(null);

  const [marketValueReport, setMarketValueReport] = useState();
  const [marketValueReportLoading, setMarketValueReportLoading] =
    useState(false);
  const [marketValueReportError, setMarketValueReportError] = useState(null);

  const [exogeneousInputData, setExogeneousInputData] = useState(null);
  const [exogeneousInputDataLoading, setExogeneousInputDataLoading] =
    useState(false);
  const [exogeneousInputDataError, setExogeneousInputDataError] =
    useState(null);

  const [sensitivityGapData, setSensitivityGapData] = useState([]);
  const [sensitivityGapDataLoading, setSensitivityGapDataLoading] =
    useState(false);
  const [sensitivityGapDataError, setSensitivityGapDataError] = useState(null);

  const [liquidityGapData, setLiquidityGapData] = useState([]);
  const [liquidityGapDataLoading, setLiquidityGapDataLoading] = useState(false);
  const [liquidityGapDataError, setLiquidityGapDataError] = useState(null);

  const [balanceSheetData, setBalanceSheetData] = useState([]);
  const [balanceSheetDataLoading, setBalanceSheetDataLoading] = useState(false);
  const [balanceSheetDataError, setBalanceSheetDataError] = useState(null);

  const [multiledgerFXData, setMultiledgerFXData] = useState([]);
  const [multiledgerFXDataLoading, setMultiledgerFXDataLoading] =
    useState(false);
  const [multiledgerFXDataError, setMultiledgerFXDataError] = useState(null);

  useEffect(() => {
    if (selectedBusinessEntityID) {
      setRegionLoading(true);
      setRegionError(null);
      securedFetch(REGION_BY_SFC_API, {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessEntityID: selectedBusinessEntityID,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch Region By SFC");
          }
          return res.text();
        })
        .then((data) => {
          setRegion(data);
        })
        .catch((error) => {
          setRegionError(error.message);
        })
        .finally(() => {
          setRegionLoading(false);
        });
    }
  }, [selectedBusinessEntityID]);

  useEffect(() => {
    setHrFiltersDataLoading(true);
    setHrFiltersDataError(null);
    securedFetch(HEDGE_RATIO_FILTERS_API, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch Hedge Ratio Filters data");
        }
        return res.json();
      })
      .then((data) => {
        setHrFiltersData(data);
      })
      .catch((error) => {
        setHrFiltersDataError(error.message);
      })
      .finally(() => {
        setHrFiltersDataLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selectedModelDatabase) {
      setScenarioMnemonicsLoading(true);
      setScenarioMnemonicsError(null);
      securedFetch(
        HEDGE_RATIO_SCENARIO_MNEMONICS_API(
          encodeURIComponent(selectedModelDatabase),
        ),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              "Failed to fetch Scenario Mnemonics data for the selected Model Database!",
            );
          }
          return res.json();
        })
        .then((data) => {
          setScenarioMnemonicsData(data);
        })
        .catch((error) => {
          setScenarioMnemonicsError(error.message);
        })
        .finally(() => {
          setScenarioMnemonicsLoading(false);
        });
    }
  }, [selectedModelDatabase]);

  const fetchHrGapResultReport = (model) => {
    // For Demo purpose select the TFA Entity and June 2025 Month End date which has ALM_TFA_MODEL_2025_06 model database
    if (selectedBusinessEntityID && selectedModelDatabase) {
      setHrGapResultDataLoading(true);
      setHrGapResultDataError(null);
      securedFetch(HEDGE_RATIO_GAPRESULT_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessEntityID: selectedBusinessEntityID,
          modelDatabase: selectedModelDatabase,
          // scenarioMnemonics: selectedScenarioMnemonics,
          model,
          monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              "Failed to fetch Hedge Ratio Gap Result Report data",
            );
          }
          return res.json();
        })
        .then((data) => {
          setHrGapResultData(data);
          const { accountNum: hrNumeratorAccountNum } =
            data?.find(
              ({ reportingAccountName }) =>
                reportingAccountName === HR_NUMERATOR,
            ) || {};
          const { accountNum: hrDenominatorAccountNum } =
            data?.find(
              ({ reportingAccountName }) =>
                reportingAccountName === HR_DENOMINATOR,
            ) || {};
          setNumeratorAccountNum(hrNumeratorAccountNum);
          setDenominatorAccountNum(hrDenominatorAccountNum);
        })
        .catch((error) => {
          setHrGapResultDataError(error.message);
        })
        .finally(() => {
          setHrGapResultDataLoading(false);
        });
    }
  };

  const fetchAdvancedNIIResultReport = (model) => {
    // For Demo purpose select the TMCC Entity and July 2025 Month End date which has ALM_TMCC_MODEL_2025_07_SHR model database
    if (selectedBusinessEntityID && selectedModelDatabase) {
      setAdvancedNIIResultDataLoading(true);
      setAdvancedNIIResultDataError(null);
      securedFetch(ADVANCED_NII_RESULT_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessEntityID: selectedBusinessEntityID,
          modelDatabase: selectedModelDatabase,
          model,
          monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              "Failed to fetch Hedge Ratio Gap Result Report data",
            );
          }
          return res.json();
        })
        .then((data) => {
          setAdvancedNIIResultData(data);
        })
        .catch((error) => {
          setAdvancedNIIResultDataError(error.message);
        })
        .finally(() => {
          setAdvancedNIIResultDataLoading(false);
        });
    }
  };

  const fetchExogeneousInputData = (accountName) => {
    // For Demo purpose select the TMCC Entity and July 2025 Month End date which has ALM_TMCC_MODEL_2025_07_SHR model database
    if (selectedBusinessEntityID && selectedMonthEnd && accountName) {
      setExogeneousInputDataLoading(true);
      setExogeneousInputDataError(null);
      securedFetch(GET_EXOGENEOUS_INPUT_DATA, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessEntityID: selectedBusinessEntityID,
          accountName,
          monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              "Failed to fetch Hedge Ratio Gap Result Report data",
            );
          }
          return res.json();
        })
        .then((data) => {
          setExogeneousInputData(data);
        })
        .catch((error) => {
          setExogeneousInputDataError(error.message);
        })
        .finally(() => {
          setExogeneousInputDataLoading(false);
        });
    }
  };

  const fetchSensitivityGapResultReport = () => {
    if (selectedScenarioMnemonics) {
      setSensitivityGapDataLoading(true);
      setSensitivityGapDataError(null);
      securedFetch(SENSITIVITY_GAPRESULT_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          scenarioMnemonics: selectedScenarioMnemonics,
          modelDatabase: selectedModelDatabase,
          businessEntityID: selectedBusinessEntityID,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(
              "Failed to fetch Sensitivity Gap Result Report data",
            );
          }
          return res.json();
        })
        .then((data) => {
          setSensitivityGapData(data);
        })
        .catch((error) => {
          setSensitivityGapDataError(error.message);
        })
        .finally(() => {
          setSensitivityGapDataLoading(false);
        });
    }
  };

  const fetchLiquidityGapResultReport = () => {
    if (selectedScenarioMnemonics) {
      setLiquidityGapDataLoading(true);
      setLiquidityGapDataError(null);
      securedFetch(LIQUIDITY_GAPRESULT_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          scenarioMnemonics: selectedScenarioMnemonics,
          modelDatabase: selectedModelDatabase,
          businessEntityID: selectedBusinessEntityID,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch Liquidity Gap Result Report data");
          }
          return res.json();
        })
        .then((data) => {
          setLiquidityGapData(data);
        })
        .catch((error) => {
          setLiquidityGapDataError(error.message);
        })
        .finally(() => {
          setLiquidityGapDataLoading(false);
        });
    }
  };

  const fetchMarketValueReport = (model) => {
    if (selectedBusinessEntityID && selectedModelDatabase && model) {
      setMarketValueReportLoading(true);
      setMarketValueReportError(null);
      securedFetch(MARKET_VALUE_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessEntityID: selectedBusinessEntityID,
          modelDatabase: selectedModelDatabase,
          model,
          monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch Market Value Report");
          }
          return res.json();
        })
        .then((data) => {
          setMarketValueReport(data);
        })
        .catch((error) => {
          setMarketValueReportError(error.message);
        })
        .finally(() => {
          setMarketValueReportLoading(false);
        });
    }
  };

  const fetchMultiledgerFXReport = useCallback(() => {
    if (selectedBusinessEntityID && selectedMonthEnd) {
      setMultiledgerFXDataLoading(true);
      setMultiledgerFXDataError(null);
      securedFetch(MULTILEDGER_FX_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          businessEntityID: selectedBusinessEntityID,
          monthEnd: selectedMonthEnd?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch Multiledger FX Report data");
          }
          return res.json();
        })
        .then((data) => {
          setMultiledgerFXData(data);
        })
        .catch((error) => {
          setMultiledgerFXDataError(error.message);
        })
        .finally(() => {
          setMultiledgerFXDataLoading(false);
        });
    }
  }, [selectedBusinessEntityID, selectedMonthEnd]);

  const fetchBalanceSheetResultReport = (model) => {
    setBalanceSheetDataLoading(true);
    setBalanceSheetDataError(null);
    securedFetch(BALANCE_SHEET_REPORT_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        businessEntityID: selectedBusinessEntityID,
        entityID: selectedEntityID,
        modelDatabase: selectedModelDatabase,
        currency: selectedCurrency,
        model,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to Balance Sheet Result Report data");
        }
        return res.json();
      })
      .then((data) => {
        setBalanceSheetData(data);
      })
      .catch((error) => {
        setBalanceSheetDataError(error.message);
      })
      .finally(() => {
        setBalanceSheetDataLoading(false);
      });
  };

  const allowedMonthEnds = React.useMemo(() => {
    return getAllowedMonthEnds(selectedBusinessEntityID, hrFiltersData);
  }, [selectedBusinessEntityID, hrFiltersData]);

  useEffect(() => {
    setSelectedModelDatabase(null);
    setSelectedMonthEnd(null);
  }, [selectedBusinessEntityID]);

  return (
    <HedgeRatioContext.Provider
      value={{
        selectedMonthEnd,
        setSelectedMonthEnd,
        selectedBusinessEntityID,
        setSelectedBusinessEntityID,
        selectedEntityID,
        setSelectedEntityID,
        localCurrency,
        setLocalCurrency,
        selectedCurrency,
        setSelectedCurrency,
        scenarioMnemonicsData,
        setScenarioMnemonicsData,
        scenarioMnemonicsLoading,
        setScenarioMnemonicsLoading,
        scenarioMnemonicsError,
        setScenarioMnemonicsError,
        selectedScenarioMnemonics,
        setSelectedScenarioMnemonics,
        selectedModelDatabase,
        setSelectedModelDatabase,
        modelDatabases,
        setModelDatabases,
        figureDivisors,
        setFigureDivisors,
        selectedFigureDivisor,
        setSelectedFigureDivisor,
        decimalsIn,
        setDecimalsIn,
        selectedDecimalsIn,
        setSelectedDecimalsIn,
        sumOfNumerators,
        setSumOfNumerators,
        sumOfDenominators,
        setSumOfDenominators,
        hedgeRatioPercentage,
        setHedgeRatioPercentage,
        regionLoading,
        setRegionLoading,
        regionError,
        setRegionError,
        region,
        setRegion,
        hrFiltersData,
        setHrFiltersData,
        hrFiltersDataLoading,
        setHrFiltersDataLoading,
        hrFiltersDataError,
        setHrFiltersDataError,
        hrGapResultData,
        setHrGapResultData,
        hrGapResultDataLoading,
        setHrGapResultDataLoading,
        hrGapResultDataError,
        setHrGapResultDataError,
        fetchHrGapResultReport,
        allowedMonthEnds,
        numeratorAccountNum,
        setNumeratorAccountNum,
        denominatorAccountNum,
        setDenominatorAccountNum,
        exogeneousInputData,
        setExogeneousInputData,
        exogeneousInputDataLoading,
        setExogeneousInputDataLoading,
        exogeneousInputDataError,
        setExogeneousInputDataError,
        fetchExogeneousInputData,

        advancedNIIResultData,
        setAdvancedNIIResultData,
        advancedNIIResultDataLoading,
        setAdvancedNIIResultDataLoading,
        advancedNIIResultDataError,
        setAdvancedNIIResultDataError,
        fetchAdvancedNIIResultReport,

        sensitivityGapData,
        setSensitivityGapData,
        sensitivityGapDataLoading,
        setSensitivityGapDataLoading,
        sensitivityGapDataError,
        setSensitivityGapDataError,

        liquidityGapData,
        setLiquidityGapData,
        liquidityGapDataLoading,
        setLiquidityGapDataLoading,
        liquidityGapDataError,
        setLiquidityGapDataError,

        marketValueReport,
        setMarketValueReport,
        marketValueReportLoading,
        setMarketValueReportLoading,
        marketValueReportError,
        setMarketValueReportError,

        fetchSensitivityGapResultReport,

        fetchLiquidityGapResultReport,

        fetchMarketValueReport,

        balanceSheetData,
        balanceSheetDataLoading,
        balanceSheetDataError,

        fetchBalanceSheetResultReport,

        multiledgerFXData,
        multiledgerFXDataLoading,
        multiledgerFXDataError,

        fetchMultiledgerFXReport,
      }}
    >
      {children}
    </HedgeRatioContext.Provider>
  );
};

export const useHedgeRatioContext = () => useContext(HedgeRatioContext);
